import { FC, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

const DRAG_THRESHOLD = 5;

export const CatalogEntryFullRenderer: FC<{
	slides: JSX.Element[];
}> = ({ slides }) => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [startX, setStartX] = useState(0);
	const [startScrollLeft, setStartScrollLeft] = useState(0);
	const hasMovedRef = useRef(false);

	useEffect(() => {
		const container = scrollRef.current;
		if (!container) return;

		const onMouseDown = (e: MouseEvent) => {
			setIsDragging(true);
			hasMovedRef.current = false;
			setStartX(e.clientX);
			setStartScrollLeft(container.scrollLeft);
		};

		const onMouseMove = (e: MouseEvent) => {
			if (!isDragging) return;
			const distance = e.clientX - startX;
			if (Math.abs(distance) > DRAG_THRESHOLD) {
				hasMovedRef.current = true;
			}
			container.scrollLeft = startScrollLeft - distance;
		};

		const onMouseUp = () => {
			setIsDragging(false);
			setTimeout(() => {
				hasMovedRef.current = false;
			}, 100);
		};

		const onClickCapture = (e: MouseEvent) => {
			if (hasMovedRef.current) {
				e.preventDefault();
				e.stopPropagation();
			}
		};

		container.addEventListener('mousedown', onMouseDown);
		container.addEventListener('mousemove', onMouseMove);
		container.addEventListener('mouseup', onMouseUp);
		container.addEventListener('mouseleave', onMouseUp);
		container.addEventListener('click', onClickCapture, true);

		return () => {
			container.removeEventListener('mousedown', onMouseDown);
			container.removeEventListener('mousemove', onMouseMove);
			container.removeEventListener('mouseup', onMouseUp);
			container.removeEventListener('mouseleave', onMouseUp);
			container.removeEventListener('click', onClickCapture, true);
		};
	}, [isDragging, startX, startScrollLeft]);

	return (
		<Box
			ref={scrollRef}
			sx={{
				display: 'flex',
				gap: '10px',
				paddingBottom: '10px',
				overflowX: 'auto',
				overflowY: 'hidden',
				width: '100%',
				cursor: isDragging ? 'grabbing' : 'grab',
				userSelect: 'none',
				'&::-webkit-scrollbar': { height: '4px' },
				'&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
				'&::-webkit-scrollbar-thumb': {
					backgroundColor: '#B5B5B5',
					borderRadius: '2px',
				},
			}}
		>
			{slides}
		</Box>
	);
};
